import get from 'lodash/get'
import isNull from 'lodash/isNull'
import React from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet'
import APP_CONFIGS from '../configs/app'
import { AVENIR_FONT_CONFIG } from '../configs/fonts'
import { getOgURL } from '../utils/url'
import { exludeFromSearchCrawlers } from 'wsc/utils/common'

const DocumentHead = props => {
  // validate props
  const title = props.seoTitle || props.title || APP_CONFIGS.title
  const description = props.seoDescription || APP_CONFIGS.description

  const ogURL = props.ogURL || getOgURL()
  const ogTitle = props.ogTitle || props.title || APP_CONFIGS.title
  const ogDescription = props.ogDescription || props.seoDescription || APP_CONFIGS.description
  const imageUrl = get(props, 'ogImage.url') || get(props, 'image.url')
  const ogImageUrl = imageUrl
    ? imageUrl + (props.isContentfulImage ? '?w=1800&q=50&fm=jpg&fl=progressive' : '')
    : APP_CONFIGS.envUrl + APP_CONFIGS.logo
  const ogImageDescription =
    get(props, 'ogImage.title') || get(props, 'image.title', APP_CONFIGS.description)
  const ogImageWidth = get(props, 'ogImage.width') || get(props, 'image.width', 296)
  const ogImageHeight = get(props, 'ogImage.height') || get(props, 'image.height', 273)
  const ogVideoUrl = get(props, 'ogVideo.url', null)
  const ogVideoType = get(props, 'ogVideo.type', null)
  const ogVideoWidth = get(props, 'ogVideo.width', null)
  const ogVideoHeight = get(props, 'ogVideo.height', null)
  const ogType = props.ogType || 'website'
  const canonicalLink = props.canonicalLink || ogURL
  const jsonLdData = props.jsonLdData
  const safeForCrossPromotion = props.safeForCrossPromotion
  const socialDescription = props.socialDescription

  let firstLoad = true

  return (
    <>
      <Helmet
        onChangeClientState={() => {
          if (firstLoad) {
            window.dataLayer = window.dataLayer || []
            window.dataLayer.push(
              // The default value of url on GTM is not matched with current URL on browser.
              // It may be delay. So, we set it from frontend instead.
              // Initializing the pageViewHistory array so that we can push URLs into it
              {
                pageviewURL: window.location.href,
                pageViewHistory: [],
              },
              // Below is the way to push values to an array in GTM
              ['pageViewHistory.push', window.location.href],
              // Calling DocumentHeadLoaded event last so that pageViewHistory is populated in the dataLayer for this the event
              { event: 'DocumentHeadLoaded' }
            )

            firstLoad = false
          }
        }}
      >
        {/* seo tags */}
        {exludeFromSearchCrawlers(props.internalTags) && (
          <meta name="robots" content="noindex,nofollow"></meta>
        )}
        <title>{`${title} | ${APP_CONFIGS.name}`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalLink} />
        {/* prefetch font */}
        {AVENIR_FONT_CONFIG.map(style => {
          return <link rel="prefetch" href={style.url} as="font" key={style.url} />
        })}
        {/* og tags */}
        <meta property="og:url" content={ogURL} />
        <meta property="og:site_name" content={APP_CONFIGS.name} />
        <meta property="og:type" content={ogType} />
        <meta property="og:locale" content="en_US" />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:image:alt" content={ogImageDescription} />
        <meta property="og:image:width" content={ogImageWidth} />
        <meta property="og:image:height" content={ogImageHeight} />
        {!isNull(ogVideoUrl) && <meta property="og:video" content={ogVideoUrl} />}
        {!isNull(ogVideoType) && <meta property="og:video:type" content={ogVideoType} />}
        {!isNull(ogVideoWidth) && <meta property="og:video:width" content={ogVideoWidth} />}
        {!isNull(ogVideoHeight) && <meta property="og:video:height" content={ogVideoHeight} />}

        {/* twitter tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@Momdotcom" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={ogDescription} />
        <meta name="twitter:image" content={ogImageUrl} />
        <meta name="twitter:image:alt" content={ogImageDescription} />
        {/* facebook */}
        <meta property="fb:app_id" content={APP_CONFIGS.facebookAppId} />

        {/* True Anthem */}
        {safeForCrossPromotion && <meta name="crossPromote" content={safeForCrossPromotion} />}
        <meta name="FacebookShareMessage" content={socialDescription} />

        {/* Tell prerender the page is ready*/}
        <script> window.prerenderReady = true; </script>
        {/* JSON-LD Tag*/}
        <script type="application/ld+json">
          {JSON.stringify(
            jsonLdData
              ? jsonLdData
              : {
                  '@context': 'http://schema.org',
                  '@type': 'WebPage',
                  headline: title,
                  url: ogURL,
                  publisher: {
                    '@type': 'Organization',
                    name: APP_CONFIGS.name,
                    logo: {
                      '@type': 'ImageObject',
                      url: APP_CONFIGS.envUrl + APP_CONFIGS.logo,
                    },
                  },
                }
          )}
        </script>
      </Helmet>
    </>
  )
}

DocumentHead.propTypes = {
  title: PropTypes.string,
  image: PropTypes.shape({
    url: PropTypes.string,
    title: PropTypes.string,
  }),
  publishDate: PropTypes.string,
  // open graph
  ogTitle: PropTypes.string,
  ogURL: PropTypes.string,
  ogType: PropTypes.string,
  ogDescription: PropTypes.string,
  ogImage: PropTypes.shape({
    url: PropTypes.string,
    title: PropTypes.string,
  }),
  isContentfulImage: PropTypes.bool,
  // search engine
  seoTitle: PropTypes.string,
  seoDescription: PropTypes.string,
  socialDescription: PropTypes.string,
  canonicalLink: PropTypes.string,
  // main category
  mainCategorySlug: PropTypes.string,
  // sub category
  relatedCategorySlugs: PropTypes.arrayOf(PropTypes.string),
  // JSON-LD Tag
  jsonLdData: PropTypes.object,
}

DocumentHead.defaultProps = {
  title: null,
  image: null,
  publishDate: null,
  // open graph
  ogTitle: null,
  ogURL: null,
  ogType: null,
  ogDescription: null,
  ogImage: null,
  isContentfulImage: true,
  // search engine
  seoTitle: null,
  seoDescription: null,
  socialDescription: null,
  canonicalLink: null,
  // main category
  mainCategorySlug: null,
  // sub category
  relatedCategorySlugs: null,
  // JSON-LD Tag
  jsonLdData: null,
}

export default DocumentHead
